/**
 * @file serverTranslations.ts
 * Centralized server-side translation function for business rules validation
 * Used by backend services that need business rules translations
 */

/**
 * Server-side translation function for business rules
 * This provides a simple fallback translation system for Node.js services
 * when proper i18next integration isn't available
 */
export function createServerTranslator(locale = 'en-US') {
  return (key: string, params?: Record<string, any>): string => {
    // Note: Currently only supports English, locale parameter reserved for future i18n support
    // Locale parameter is unused but reserved for future i18n support
    // Complete business rules translation messages
    const messages: Record<string, string> = {
      // Common business rule messages
      'business_rules.common.field_required': '{{propertyDisplayName}} is required',
      'business_rules.common.field_must_equal': '{{propertyDisplayName}} must equal {{expectedValue}}',
      'business_rules.common.field_must_not_equal': '{{propertyDisplayName}} must not equal {{expectedValue}}',
      'business_rules.common.field_must_be_greater': '{{propertyDisplayName}} must be greater than {{expectedValue}}',
      'business_rules.common.field_must_be_less': '{{propertyDisplayName}} must be less than {{expectedValue}}',
      'business_rules.common.field_must_be_greater_or_equal': '{{propertyDisplayName}} must be greater than or equal to {{expectedValue}}',
      'business_rules.common.field_must_be_less_or_equal': '{{propertyDisplayName}} must be less than or equal to {{expectedValue}}',
      'business_rules.common.field_must_contain': '{{propertyDisplayName}} must contain {{expectedValue}}',
      'business_rules.common.field_must_exist': '{{propertyDisplayName}} must exist',
      'business_rules.common.field_must_be_true': '{{propertyDisplayName}} must be true',
      'business_rules.common.field_must_be_false': '{{propertyDisplayName}} must be false',
      'business_rules.common.field_must_be_one_of': '{{propertyDisplayName}} must be one of {{expectedValue}}',
      'business_rules.common.field_must_not_be_one_of': '{{propertyDisplayName}} must not be one of {{expectedValue}}',
      'business_rules.common.validation_failed': 'Validation failed',
      'business_rules.common.empty_value': 'N/A',

      // Calendar date validation messages
      'business_rules.calendar_date_validation.name': 'Calendar Date Validation',
      'business_rules.calendar_date_validation.description': 'Ensures that end date is after start date for calendar items',
      'business_rules.calendar_date_validation.main_message': 'Calendar date validation failed',
      'business_rules.calendar_date_validation.start_before_end': 'Start date must be before end date',

      // Operators
      'business_rules.operators.and': 'and',
      'business_rules.operators.or': 'or',
      'business_rules.operators.equals': 'equals',
      'business_rules.operators.not_equals': 'not equals',
      'business_rules.operators.greater_than': 'greater than',
      'business_rules.operators.less_than': 'less than',
      'business_rules.operators.greater_than_or_equal': 'greater than or equal to',
      'business_rules.operators.less_than_or_equal': 'less than or equal to',
      'business_rules.operators.contains': 'contains',
      'business_rules.operators.exists': 'exists',
      'business_rules.operators.is_true': 'is true',
      'business_rules.operators.is_false': 'is false',
      'business_rules.operators.in': 'in',
      'business_rules.operators.not_in': 'not in'
    };

    let message = messages[key] || key;

    // Replace parameter placeholders
    if (params) {
      for (const [paramKey, value] of Object.entries(params)) {
        const placeholder = new RegExp(`\\{\\{${paramKey}\\}\\}`, 'g');
        message = message.replace(placeholder, String(value));
      }
    }

    return message;
  };
}

/**
 * Default English translator for server-side use
 */
export const serverTranslator = createServerTranslator('en-US');